import { useEffect, useState } from "react";
import { Button, Field, Input, Label } from "@headlessui/react";
import { PencilIcon, UsersIcon } from "@heroicons/react/24/outline";
import { UserRole } from "../../../app/userSlice";
import { useAppSelector } from "../../../app/hooks";
import RequiredField from "../../../components/RequiredField";
import ModalForm from "../../../components/ModalForm";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import { SchoolSpaViewModel, useAdjustSeatLimitMutation } from "../schoolsApiSlice";

interface SeatCountProps {
	schoolInfo: SchoolSpaViewModel;
	setErrorMessage: (message: string) => void;
}

export default function SeatCount({ schoolInfo, setErrorMessage }: SeatCountProps) {
	const { role } = useAppSelector((state) => state.user);

	const [seatLimit, setSeatLimit] = useState(schoolInfo.totalSeats);
	const [updatedSeatLimit, setUpdatedSeatLimit] = useState(schoolInfo.totalSeats);
	const [isSeatLimitFormOpen, setIsSeatLimitFormOpen] = useState(false);
	const [disableSeatLimitConfirm, setDisableSeatLimitConfirm] = useState(false);

	const [adjustSeatLimit] = useAdjustSeatLimitMutation();

	function handleAdjustSeatLimit() {
		if (updatedSeatLimit >= 0) {
			adjustSeatLimit({ schoolId: schoolInfo.id, seatLimit: updatedSeatLimit })
				.unwrap()
				.then(() => {
					setIsSeatLimitFormOpen(false);
					setSeatLimit(updatedSeatLimit);
				})
				.catch((error) => {
					setIsSeatLimitFormOpen(false);
					setErrorMessage(`Failed to save the seat limit. ${error.data}`);
				});
		}
	}

	return (
		<div className="flex items-center">
			<UsersIcon className="size-18" />
			<div className="ml-4">
				<p>Seat Count:</p>
				<div className="flex items-center">
					<span className="tracking-widest text-2xl ml-2 mr-4">
						<span className="text-2xl font-medium">{schoolInfo.takenSeats}</span>/
						<span className="text-xl">{seatLimit}</span>
					</span>
					{role === UserRole.Admin && (
						<div>
							<Button
								className="border-2 p-1 rounded-md shadow-sm border-gray-900/20 cursor-pointer"
								onClick={() => setIsSeatLimitFormOpen(true)}>
								<PencilIcon className="size-5 " />
							</Button>

							{isSeatLimitFormOpen && (
								<ModalForm
									open={isSeatLimitFormOpen}
									title="Adjust Seat Limit"
									form={
										<SeatLimitForm
											minAllowed={schoolInfo.takenSeats}
											currentLimit={seatLimit}
											updateLimit={setUpdatedSeatLimit}
											disableConfirm={setDisableSeatLimitConfirm}
										/>
									}
									confirmLabel="Save"
									disableConfirm={disableSeatLimitConfirm}
									onConfirm={handleAdjustSeatLimit}
									onCancel={() => setIsSeatLimitFormOpen(false)}
								/>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

interface SeatLimitFormProps {
	minAllowed: number;
	currentLimit: number;
	updateLimit: (count: number) => void;
	disableConfirm: (disable: boolean) => void;
}

function SeatLimitForm(props: SeatLimitFormProps) {
	const [seatLimit, setSeatLimit] = useState(props.currentLimit);
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		props.updateLimit(seatLimit);
	}, [seatLimit]);

	useEffect(() => {
		if (seatLimit < props.minAllowed || isNaN(seatLimit)) {
			setErrorMessage(
				`The seat limit must be at least as high as the number of currently enrolled students in the school, which is ${props.minAllowed}.`
			);
			props.disableConfirm(true);
		} else {
			setErrorMessage("");
			props.disableConfirm(false);
		}
	}, [seatLimit]);

	return (
		<div className="mt-3">
			<AlertMessage
				className="mb-2 text-sm"
				type={AlertLevel.Warning}
				message={errorMessage}
				showIcon={true}
			/>
			<Field>
				<Label htmlFor="seatLimit" className="font-semibold text-sm">
					Seat Limit
					<RequiredField />
				</Label>
				<Input
					id="seatLimit"
					name="seatLimit"
					type="number"
					min={props.minAllowed}
					max={9999}
					required
					value={seatLimit}
					className={`${isNaN(seatLimit) || seatLimit < props.minAllowed || seatLimit > 9999 ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} mt-2 resize-none w-full h-10 text-sm rounded-sm border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset`}
					onChange={(e) => setSeatLimit(Number(e.target.value))}></Input>
			</Field>
		</div>
	);
}
